import axios from "axios";
import baseUrlNew from "../BaseUrl/index";
import {
  answersMapFromInspectionCar,
  mergeAnswerMaps,
  toProductId,
  toSignedCarId,
} from "./checklistAnswers";

/**
 * Media + checklist progress helpers for inspection car steps.
 *
 * Dashboard `inspect` comes from the API (`inspection_cars` / `inspection_product`).
 * The client also computes a local % from completed media steps + answered
 * checklist sections so the bar is not stuck at 0 when location.state.inspect
 * was dropped on navigation.
 *
 * ID rules (Django signing):
 * - Signed car_id: track_old, check_product_photos, product_images_Details,
 *   inspection_product, create_selected_options
 * - Numeric id: getselected_inspection
 */

export async function fetchDamagePoints(productId) {
  const id = toSignedCarId(productId);
  if (id == null) return [];
  try {
    const response = await axios.post(
      `${baseUrlNew.baseUrl}api/v1/track_old/`,
      { product: id }
    );
    const data = response?.data?.data;
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function fetchCarPhotosAvailable(productId) {
  const id = toSignedCarId(productId);
  if (id == null) return false;
  try {
    const response = await axios.post(
      `${baseUrlNew.baseUrl}api/v1/check_product_photos/`,
      { pk: id }
    );
    return Boolean(response?.data?.data?.available);
  } catch {
    return false;
  }
}

/** Count captured vehicle photos from product image details. */
export async function fetchCarPhotoCount(productId) {
  const id = toSignedCarId(productId);
  if (id == null) return 0;
  try {
    const response = await axios.get(
      `${baseUrlNew.baseUrl}api/v1/product_images_Details/${encodeURIComponent(
        String(id)
      )}`
    );
    const payload = response?.data?.data ?? response?.data ?? {};
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      return 0;
    }

    let count = 0;
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (Array.isArray(value)) {
        count += value.filter(
          (item) => item && (item.image || item.image_data || item.id)
        ).length;
      }
    });
    return count;
  } catch {
    return 0;
  }
}

export async function fetchProductInspect(productId) {
  const id = toSignedCarId(productId);
  if (id == null) return null;
  try {
    const response = await axios.post(
      `${baseUrlNew.baseUrl}api/v1/inspection_product`,
      { id }
    );
    const row = response?.data?.data?.[0];
    if (!row) return null;
    return {
      inspect: Number(row.inspect) || 0,
      name: row.name,
      report: row.report,
    };
  } catch {
    return null;
  }
}

export async function fetchMediaStepStatus(productId) {
  const [damagePoints, photosDone, photosCount] = await Promise.all([
    fetchDamagePoints(productId),
    fetchCarPhotosAvailable(productId),
    fetchCarPhotoCount(productId),
  ]);

  return {
    damagePoints,
    damageCount: damagePoints.length,
    damageDone: damagePoints.length > 0,
    photosCount,
    photosDone: photosDone || photosCount > 0,
    // Audio has no reliable "list" endpoint used by steps today.
    audioDone: false,
  };
}

/**
 * Overall inspection progress.
 *
 * Weighting:
 * - 3 media steps (damage points, car photos, audio) — each 0 or 1
 * - every checklist question — answered count / total question count
 *
 * Example with 2 sections (3 + 6 questions) and no media:
 *   1 answered → 1 / (3 + 9) = 8%
 * All questions + 2 media, no audio:
 *   (2 + 9) / (3 + 9) = 92%
 *
 * Note: API `inspect` on inspection_cars is typically questions-only
 * (1/9 ≈ 11%, all questions ≈ 100%) and ignores media — prefer this
 * client formula for the steps UI.
 */
export function computeLocalInspectPercent({
  mediaStatus,
  selectedBySection,
  checklistSections,
  checklistLength,
  mediaStepCount = 3,
}) {
  let mediaDone = 0;
  if (mediaStatus?.damageDone) mediaDone += 1;
  if (mediaStatus?.photosDone) mediaDone += 1;
  if (mediaStatus?.audioDone) mediaDone += 1;

  const sections = Array.isArray(checklistSections) ? checklistSections : [];
  let questionTotal = 0;
  let questionAnswered = 0;

  if (sections.length) {
    sections.forEach((item) => {
      const total = Math.max(0, Number(item?.questionCount) || 0);
      if (!total) return;
      const answers =
        selectedBySection?.[item.id] ||
        selectedBySection?.[String(item.id)] ||
        selectedBySection?.[item.title] ||
        [];
      const answered = Array.isArray(answers)
        ? Math.min(answers.length, total)
        : 0;
      questionTotal += total;
      questionAnswered += answered;
    });
  } else {
    // Legacy fallback: treat each section with any answer as one unit.
    const checklistDone = Object.values(selectedBySection || {}).filter(
      (answers) => Array.isArray(answers) && answers.length > 0
    ).length;
    questionTotal = Math.max(0, checklistLength || 0);
    questionAnswered = Math.min(checklistDone, questionTotal);
  }

  const total = mediaStepCount + questionTotal;
  if (total <= 0) return 0;

  return Math.max(
    0,
    Math.min(
      100,
      Math.round(((mediaDone + questionAnswered) / total) * 100)
    )
  );
}

export function resolveInspectPercent(apiInspect, localInspect, options = {}) {
  const api = Math.max(0, Math.min(100, Number(apiInspect) || 0));
  const local = Math.max(0, Math.min(100, Number(localInspect) || 0));

  // Car steps / accurate client progress: don't let questions-only API
  // report 100% when media (e.g. audio) is still missing.
  if (options.preferLocal) return local;

  return Math.max(api, local);
}

const inspectCache = new Map();

export function readCachedInspect(productId) {
  if (!productId) return null;
  const key = String(productId);
  if (inspectCache.has(key)) return inspectCache.get(key);
  try {
    const raw = sessionStorage.getItem(`pi-inspect-pct-${key}`);
    if (raw == null) return null;
    const value = Math.max(0, Math.min(100, Number(raw) || 0));
    inspectCache.set(key, value);
    return value;
  } catch {
    return null;
  }
}

export function writeCachedInspect(productId, value) {
  if (!productId) return;
  const key = String(productId);
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  inspectCache.set(key, pct);
  try {
    sessionStorage.setItem(`pi-inspect-pct-${key}`, String(pct));
  } catch {
    /* ignore */
  }
}

export async function fetchSelectedAnswersMap(productId) {
  const id = toProductId(productId);
  if (id == null) return {};
  try {
    const response = await axios.post(
      `${baseUrlNew.baseUrl}api/v1/getselected_inspection/`,
      { id }
    );
    const sections = response?.data?.data?.data || [];
    const map = {};
    sections.forEach((section) => {
      const answers = [];
      (section.inspection_title || []).forEach((q) => {
        const selected = q.selected_inspection_question?.[0];
        const label =
          selected?.choice_select_ ||
          selected?.choice_select ||
          selected?.choice ||
          "";
        if (label) answers.push(`${q.question}: ${label}`);
      });
      map[section.id] = answers;
    });
    return map;
  } catch {
    return null;
  }
}

let activeChecklistCache = null;
let activeChecklistPromise = null;

/**
 * Checklist section titles from the API include empty templates.
 * Only return sections that have at least one question configured.
 */
export async function fetchActiveChecklistSections({ force = false } = {}) {
  if (!force && activeChecklistCache) {
    return activeChecklistCache;
  }
  if (!force && activeChecklistPromise) {
    return activeChecklistPromise;
  }

  activeChecklistPromise = (async () => {
    try {
      const response = await axios.get(
        `${baseUrlNew.baseUrl}api/v1/inspection_title/`
      );
      const payload = response?.data;
      const titles = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      const probed = await Promise.all(
        titles.map(async (item) => {
          try {
            const res = await axios.post(
              `${baseUrlNew.baseUrl}api/v1/getoption_inspection/`,
              { id: item.id }
            );
            const questions = res?.data?.data?.inspection_title || [];
            if (!questions.length) return null;
            return {
              ...item,
              questionCount: questions.length,
            };
          } catch {
            return null;
          }
        })
      );

      activeChecklistCache = probed.filter(Boolean);
      return activeChecklistCache;
    } catch {
      activeChecklistCache = [];
      return activeChecklistCache;
    } finally {
      activeChecklistPromise = null;
    }
  })();

  return activeChecklistPromise;
}

export async function fetchChecklistLength() {
  const sections = await fetchActiveChecklistSections();
  return sections.length;
}

/**
 * Dashboard / home progress for one vehicle.
 * Same formula as car steps: (media done + questions answered) /
 * (3 media + total checklist questions).
 */
export async function fetchCarDashboardProgress(
  productId,
  checklistSections,
  carRow
) {
  if (!productId) return 0;

  const sections = Array.isArray(checklistSections)
    ? checklistSections
    : await fetchActiveChecklistSections();

  const [mediaStatus, selectedBySection] = await Promise.all([
    fetchMediaStepStatus(productId),
    fetchSelectedAnswersMap(productId),
  ]);

  const fromCar = answersMapFromInspectionCar(carRow);
  const answers = mergeAnswerMaps(fromCar, selectedBySection || {});

  return computeLocalInspectPercent({
    mediaStatus,
    selectedBySection: answers,
    checklistSections: sections,
    checklistLength: sections.length,
    mediaStepCount: 3,
  });
}

/**
 * Enrich car list rows with computed inspect %.
 * Processes priorityIds first, then the rest, with higher concurrency.
 */
export async function enrichCarsWithInspectProgress(
  cars,
  onUpdate,
  options = {}
) {
  if (!Array.isArray(cars) || cars.length === 0) return cars;

  const sections = await fetchActiveChecklistSections();
  const next = cars.map((car) => ({ ...car }));
  const byId = new Map(
    next.map((car, index) => [String(car.car_id || car.id), index])
  );

  const priorityIds = (options.priorityIds || [])
    .map(String)
    .filter((id) => byId.has(id));
  const remainingIds = next
    .map((car) => String(car.car_id || car.id))
    .filter((id) => id && !priorityIds.includes(id));
  const orderedIds = [...priorityIds, ...remainingIds];
  const batchSize = options.batchSize || 6;

  const applyPct = (carId, pct) => {
    const index = byId.get(String(carId));
    if (index == null) return;
    const car = next[index];
    const value = Math.max(0, Math.min(100, Number(pct) || 0));
    writeCachedInspect(carId, value);
    next[index] = {
      ...car,
      inspect: value,
      inspectReady: true,
    };
  };

  for (let i = 0; i < orderedIds.length; i += batchSize) {
    const batch = orderedIds.slice(i, i + batchSize);
    await Promise.all(
      batch.map(async (carId) => {
        const index = byId.get(String(carId));
        const carRow = index != null ? next[index] : null;
        const pct = await fetchCarDashboardProgress(carId, sections, carRow);
        applyPct(carId, pct);
      })
    );
    if (typeof onUpdate === "function") {
      onUpdate(next.map((car) => ({ ...car })));
    }
  }

  return next;
}
